import { FC, useEffect, useMemo, useState } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import { CalendarIcon, CloseIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { DatePicker } from '../../../../../components/date-picker/index';
import { FormInput } from '../../../../../components/input/index';
import { useDebounce, useFormat } from '../../../../../hooks/index';
import { SharePlanType } from '../../../../../types/pool-plans.types';
import { toNumber } from '../../../../../utils/to-number.util';
import { FormSchema } from '../../exercise-form.validation';
import { GrantCombobox, StakeholderCombobox } from '../../form-components/index';

export type SarTableRowProps = {
  availableIssuedShares: number;
  availableNoOfSar: number;
  availableSarPayout: number;
  companyId: string;
  control: Control<FormSchema>;
  formData: UseFormGetValues<FormSchema>;
  handleRemoveRow: () => void;
  handleStakeholderChange: () => void;
  id: string;
  rowIndex: number;
  selectedGrantIds?: string[];
  setFormData: UseFormSetValue<FormSchema>;
  totalItems: number;
};

export const SarTableRow: FC<SarTableRowProps> = ({
  companyId,
  control,
  setFormData,
  rowIndex,
  handleStakeholderChange,
  handleRemoveRow,
  totalItems,
  availableNoOfSar,
  availableIssuedShares,
  availableSarPayout,
  formData,
  selectedGrantIds,
}) => {
  const sarItem = useWatch({ control, name: `stepOne.sar.sarItems.${rowIndex}` });
  const { format } = useFormat();

  const checkSharesCount = useMemo(() => {
    const sarCount = Number(sarItem.sarCount);
    const sarPayout = Number(sarItem.sarPayout);
    const issuedShares = Number(sarItem.issuedShares);
    const sharePrice = Number(sarItem.sharePrice);
    const sarBasePrice = Number(sarItem.grant?.grantItem?.plan?.sarBasePrice);
    const priceDifference = sharePrice && sarBasePrice ? Number(sharePrice - sarBasePrice) : 1;
    const showError =
      sarCount &&
      issuedShares &&
      sarPayout &&
      priceDifference &&
      sarCount !== sarPayout / priceDifference + (issuedShares * sharePrice) / priceDifference;

    return !!showError || false;
  }, [sarItem]);

  const showNegativeValuesError =
    sarItem.issuedShares < 0 ||
    sarItem.sarPayout < 0 ||
    availableIssuedShares < 0 ||
    availableSarPayout < 0;

  const [debounceTrigger, setDebounceTrigger] = useState<'issuedShares' | 'sarPayout' | null>(null);

  const { debounceCallback: debouncePayout } = useDebounce({
    callback: (calculatedPayout: number) => {
      setFormData(`stepOne.sar.sarItems.${rowIndex}.sarPayout`, calculatedPayout);
      setDebounceTrigger(null);
    },
    debounce: 1000,
  });

  const { debounceCallback: debounceIssuedShares } = useDebounce({
    callback: (calculatedIssuedShares: number) => {
      setFormData(`stepOne.sar.sarItems.${rowIndex}.issuedShares`, calculatedIssuedShares);
      setDebounceTrigger(null);
    },
    debounce: 1000,
  });

  useEffect(() => {
    if (availableIssuedShares && debounceTrigger === 'sarPayout') {
      if (availableIssuedShares + toNumber(sarItem.issuedShares) < 0) {
        return;
      }
      debounceIssuedShares(availableIssuedShares + toNumber(sarItem.issuedShares));
    } else if (availableSarPayout && debounceTrigger === 'issuedShares') {
      if (availableSarPayout + toNumber(sarItem.sarPayout) < 0) {
        return;
      }
      debouncePayout(availableSarPayout + toNumber(sarItem.sarPayout));
    }
  }, [
    availableIssuedShares,
    availableSarPayout,
    debounceIssuedShares,
    debouncePayout,
    debounceTrigger,
    sarItem.issuedShares,
    sarItem.sarPayout,
  ]);

  return (
    <div className="grid w-full grid-cols-[repeat(9,143.3px)_repeat(1,40px)] rounded-t-lg">
      <div className="px-4 pb-10 pt-7">
        <StakeholderCombobox
          companyId={companyId}
          control={control}
          formData={formData}
          handleStakeholderChange={handleStakeholderChange}
          name={`stepOne.sar.sarItems.${rowIndex}.stakeholder`}
          planType={SharePlanType.SAR}
          setFormData={setFormData}
        />
      </div>
      <div className="px-4 pb-10 pt-7">
        <GrantCombobox
          companyId={companyId}
          control={control}
          name={`stepOne.sar.sarItems.${rowIndex}.grant`}
          selectedGrantIds={selectedGrantIds}
          sharePlanType={SharePlanType.SAR}
          stakeholderId={sarItem?.stakeholder?.id || ''}
        />
      </div>
      <div className="px-4 pb-10 pt-[22px]">
        <DatePicker
          className="px-0"
          onSelect={(date) => setFormData(`stepOne.sar.sarItems.${rowIndex}.date`, date)}
          value={sarItem?.date || null}
        >
          <FormInput
            autoComplete="off"
            control={control}
            disabledInput
            icon={<CalendarIcon className="size-5" iconColor="#344054" />}
            inputClassName="h-10 w-[140px]"
            name={`stepOne.sar.sarItems.${rowIndex}.date`}
            transform={(value) => {
              return format(value, 'dd/MM/yyyy');
            }}
            wrapperClassName="w-full"
          />
        </DatePicker>
      </div>

      <div className="flex items-center px-8 pb-10 pt-7">
        <span className="text-sm font-[450] text-gray-700">
          {sarItem?.grant?.grantItem?.plan?.pool?.shareClass?.name}
        </span>
      </div>

      <div className="flex items-center px-4 pb-10 pt-7">
        <span className="text-sm font-[450] text-gray-700">
          ${toNumber(sarItem?.grant?.grantItem?.plan?.sarBasePrice)}
        </span>
      </div>

      <div className="px-4 pb-10 pt-6">
        <FormInput
          control={control}
          iconBeforeInput={<span>$</span>}
          iconBeforeInputClassName="mt-1 -mr-1"
          name={`stepOne.sar.sarItems.${rowIndex}.sharePrice`}
          shouldFormatNumber
          wrapperClassName="h-10 "
        />
      </div>

      <div className="mt-6 flex flex-col gap-2 px-4">
        <FormInput
          control={control}
          isShownErrorMessage={false}
          name={`stepOne.sar.sarItems.${rowIndex}.sarCount`}
          shouldFormatNumber
          wrapperClassName="h-10"
        />
        <span className="text-xs font-[450] text-gray-600">
          <AppFormattedMessage id="AVAILABLE" /> {availableNoOfSar}
        </span>
      </div>

      <div className="mt-6 flex flex-col gap-2 px-4">
        <FormInput
          control={control}
          isShownErrorMessage={false}
          name={`stepOne.sar.sarItems.${rowIndex}.sarPayout`}
          onChange={() => setDebounceTrigger('sarPayout')}
          shouldFormatNumber
          wrapperClassName="h-10"
        />
        <span className="text-xs font-[450] text-gray-600">
          <AppFormattedMessage id="AVAILABLE" /> {availableSarPayout}
        </span>
      </div>

      <div className="mt-6 flex flex-col gap-2 px-4">
        <FormInput
          control={control}
          isShownErrorMessage={false}
          name={`stepOne.sar.sarItems.${rowIndex}.issuedShares`}
          onChange={() => setDebounceTrigger('issuedShares')}
          shouldFormatNumber
          wrapperClassName="h-10"
        />
        <span className="text-xs font-[450] text-gray-600">
          <AppFormattedMessage id="AVAILABLE" /> {Math.floor(availableIssuedShares)}
        </span>
      </div>

      {totalItems > 0 && (
        <>
          <Button
            className="flex h-full w-full items-center justify-center pb-5"
            onClick={handleRemoveRow}
            styleType="NONE"
          >
            <CloseIcon />
          </Button>
          {checkSharesCount && !showNegativeValuesError && (
            <span className="w-[400px] px-4 pb-4 text-xs font-[450] text-fireside-600">
              <AppFormattedMessage id="ALL_SARS_MUST_BE_DISTRIBUTED" />
            </span>
          )}
          {showNegativeValuesError && (
            <span className="w-[400px] px-4 pb-4 text-xs font-[450] text-fireside-600">
              <AppFormattedMessage id="MAXIMUM_AMOUNT_EXCEEDED" />
            </span>
          )}
        </>
      )}
    </div>
  );
};
